import { CenteredLazyLoading } from "@/controls/LazyLoading";
import type { Recipe } from "@/pages/sy/models/Recipe";
import {
  useSetRecipeAsCurrentMutation,
  useDeleteRecipeMutation,
  useDeleteRecipeFamilyMutation,
  useSetRecipeFamilyEnableMutation,
  useUpgradeRecipeMutation,
  useCloneRecipeMutation,
} from "@/pages/sy/hooks/queries/useRecipeMutations";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { TextFieldSearchFilter } from "@/shared/TextFieldSearchFilter";
import {
  ErrorModal,
  HelmetDexteel,
  MesfModal,
  useContextMenuMESF,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Group, Panel } from "react-resizable-panels";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useResizablePanels } from "@/pages/sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "@/pages/sy/components/ResizablePanelSeparator";
import { useRecipeOptionsFunctions } from "../hooks/useRecipeOptionsFunctions";
import { useRecipeTreeTableData } from "../hooks/useRecipeTreeTableData";
import { ROW_TYPE, toRecipe } from "../models/RecipeTreeRow";
import type { RecipeTreeRow } from "../models/RecipeTreeRow";
import { MODAL_STATE, VIEW_MODE } from "../models/recipe-types";
import type { ModalData, ModalState, ViewMode } from "../models/recipe-types";
import { transformRecipesToTreeData } from "../utils/recipe-tree-transform";
import { RecipeDetails } from "./RecipeDetails";

interface Props {
  isLoading: boolean;
  recipes: Recipe[];
  invalidateRecipes: () => void;
  basePath: string;
  recipeId: string | undefined;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

export const TableRecipe = ({
  isLoading,
  recipes,
  invalidateRecipes,
  basePath,
  recipeId,
  setViewMode,
}: Props) => {
  const navigate = useNavigate();
  const { state: utlState } = useUTLSettingsContext();

  // === TanStack Query Mutations ===
  const setAsCurrentMutation = useSetRecipeAsCurrentMutation();
  const deleteMutation = useDeleteRecipeMutation();
  const deleteFamilyMutation = useDeleteRecipeFamilyMutation();
  const setFamilyEnableMutation = useSetRecipeFamilyEnableMutation();
  const upgradeMutation = useUpgradeRecipeMutation();
  const cloneMutation = useCloneRecipeMutation();

  const [error, setError] = useState("");
  const onMutationError = (err: unknown) => setError(getErrorMessage(err));

  // Resizable panels
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "recipe-table-panels",
      defaultLayout: { table: 40, details: 60 },
    });

  // Feature flag for Clone Recipe functionality
  const enableCloneRecipe =
    get(utlState.settings, "SCRAP_ENABLE_CLONE_RECIPE", "0") === "1";

  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<ModalState>(MODAL_STATE.CLOSED);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<ModalData>({
    open: false,
    recipe: null,
  });
  const [cloneModalData, setCloneModalData] = useState<ModalData>({
    open: false,
    recipe: null,
  });
  const [upgradeModalData, setUpgradeModalData] = useState<ModalData>({
    open: false,
    recipe: null,
  });
  const [setCurrentModalData, setSetCurrentModalData] = useState<ModalData>({
    open: false,
    recipe: null,
  });
  const [deleteFamilyModalData, setDeleteFamilyModalData] = useState<{
    open: boolean;
    originalRecipeId: number | null;
    familyName: string | null;
  }>({
    open: false,
    originalRecipeId: null,
    familyName: null,
  });
  const [toggleFamilyModalData, setToggleFamilyModalData] = useState<{
    open: boolean;
    originalRecipeId: number | null;
    familyName: string | null;
    isEnable: boolean;
  }>({
    open: false,
    originalRecipeId: null,
    familyName: null,
    isEnable: false,
  });
  const [pendingSelectRecipeId, setPendingSelectRecipeId] = useState<
    number | null
  >(null);

  // Clone recipe form
  const {
    control: cloneFormControl,
    handleSubmit: handleCloneSubmit,
    reset: resetCloneForm,
    formState: { errors: cloneFormErrors, isValid: isCloneFormValid },
  } = useForm<{ recipeName: string }>({
    mode: "onChange",
    defaultValues: { recipeName: "" },
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Transform flat recipes into tree data
  const treeData = useMemo(
    () => transformRecipesToTreeData(recipes),
    [recipes],
  );

  // Handle pending recipe selection when data arrives
  useEffect(() => {
    if (pendingSelectRecipeId && gridApi) {
      const rowNode = gridApi.getRowNode(`recipe-${pendingSelectRecipeId}`);
      if (rowNode) {
        setViewMode(VIEW_MODE.EDIT);
        // If this is a child node, expand the parent first
        if (rowNode.parent && !rowNode.parent.expanded) {
          rowNode.parent.setExpanded(true);
        }
        gridApi.ensureNodeVisible(rowNode, "middle");
        rowNode.setSelected(true);
        if (rowNode.data) {
          setRecipe(toRecipe(rowNode.data));
        }
        setPendingSelectRecipeId(null);
      }
    }
  }, [treeData, pendingSelectRecipeId, gridApi]);

  // Sync local recipe state with query data when it changes (e.g., after SignalR invalidation)
  useEffect(() => {
    if (recipe?.RecipeId && recipes.length > 0) {
      const updatedRecipe = recipes.find((r) => r.RecipeId === recipe.RecipeId);
      if (updatedRecipe) {
        setRecipe(updatedRecipe);
      }
    }
  }, [recipes]);

  // Tree data accessors
  const getDataPath = useCallback((data: RecipeTreeRow) => data.path, []);
  const getRowId = useCallback(
    (params: { data: RecipeTreeRow }) => params.data.id,
    [],
  );

  const handleNewRecipe = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal(MODAL_STATE.NEW);
    setRecipe(null);
    setViewMode(VIEW_MODE.EDIT);
    navigate(`${basePath}/new`);
  };

  // Tree action handlers
  const handleCloneClick = useCallback(
    (row: RecipeTreeRow) => {
      resetCloneForm({ recipeName: "" });
      setCloneModalData({ open: true, recipe: toRecipe(row) });
    },
    [resetCloneForm],
  );

  const handleUpgradeClick = useCallback((row: RecipeTreeRow) => {
    setUpgradeModalData({ open: true, recipe: toRecipe(row) });
  }, []);

  const handleSetAsCurrentClick = useCallback((row: RecipeTreeRow) => {
    setSetCurrentModalData({ open: true, recipe: toRecipe(row) });
  }, []);

  const handleDeleteClick = useCallback((row: RecipeTreeRow) => {
    setDeleteModalData({ open: true, recipe: toRecipe(row) });
  }, []);

  const handleDeleteFamilyClick = useCallback((row: RecipeTreeRow) => {
    setDeleteFamilyModalData({
      open: true,
      originalRecipeId: row.OriginalRecipeId,
      familyName: row.RecipeName,
    });
  }, []);

  const handleToggleFamilyEnable = useCallback((row: RecipeTreeRow) => {
    if (!row.OriginalRecipeId) return;
    setToggleFamilyModalData({
      open: true,
      originalRecipeId: row.OriginalRecipeId,
      familyName: row.RecipeName,
      isEnable: !row.IsEnable,
    });
  }, []);

  const { columnDefs, autoGroupColumnDef } = useRecipeTreeTableData({
    onUpgrade: handleUpgradeClick,
    onClone: enableCloneRecipe ? handleCloneClick : undefined,
    onSetAsCurrent: handleSetAsCurrentClick,
    onDelete: handleDeleteClick,
    onDeleteFamily: handleDeleteFamilyClick,
    onToggleFamilyEnable: handleToggleFamilyEnable,
  });

  const handleSetAsCurrentFromMenu = useCallback((menuRecipe: Recipe) => {
    setSetCurrentModalData({ open: true, recipe: menuRecipe });
  }, []);

  const handleToggleEnableFromMenu = useCallback((menuRecipe: Recipe) => {
    if (!menuRecipe.OriginalRecipeId) return;
    setToggleFamilyModalData({
      open: true,
      originalRecipeId: menuRecipe.OriginalRecipeId,
      familyName: menuRecipe.RecipeName,
      isEnable: !menuRecipe.IsEnable,
    });
  }, []);

  const handleSetAsCurrent = () => {
    if (!setCurrentModalData.recipe?.RecipeId) return;

    setAsCurrentMutation.mutate(setCurrentModalData.recipe.RecipeId, {
      onSuccess: () => {
        setSetCurrentModalData({ open: false, recipe: null });
        setRecipe((prev) => {
          if (!prev) return prev;
          if (prev.RecipeId === setCurrentModalData.recipe?.RecipeId) {
            return { ...prev, IsCurrent: true, IsEnable: true };
          }
          if (
            prev.OriginalRecipeId ===
            setCurrentModalData.recipe?.OriginalRecipeId
          ) {
            return { ...prev, IsCurrent: false };
          }
          return prev;
        });
      },
      onError: onMutationError,
    });
  };

  const { getMenuOptions } = useRecipeOptionsFunctions({
    setRecipe,
    setDeleteModalData,
    handleNewRecipe,
    setUpgradeModalData,
    setCloneModalData: enableCloneRecipe ? setCloneModalData : undefined,
    onSetAsCurrent: handleSetAsCurrentFromMenu,
    onToggleEnable: handleToggleEnableFromMenu,
  });

  useEffect(() => {
    if (showModal === MODAL_STATE.EDIT) {
      setViewMode(VIEW_MODE.EDIT);
    }
  }, [showModal, setViewMode]);

  const onRowSelected = (event: RowSelectedEvent<RecipeTreeRow>) => {
    if (!event.node.isSelected()) return;

    const data = event.data;
    if (!data) return;

    const recipeData = toRecipe(data);
    setRecipe(recipeData);
    setShowModal(MODAL_STATE.EDIT);
    setViewMode(VIEW_MODE.READ);
    navigate(`${basePath}/${recipeData.RecipeId}`);
  };

  const refreshDataIfNeeded = async (force = false) => {
    const now = Date.now();
    if (force || now - lastRefreshTime > 3000 || pendingRefresh) {
      invalidateRecipes();
      setLastRefreshTime(now);
      setPendingRefresh(false);
    } else {
      setPendingRefresh(true);
    }
  };

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
    keepEditMode = false,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
    keepEditMode?: boolean;
  }) => {
    if (close) {
      setShowModal(MODAL_STATE.CLOSED);
      setRecipe(null);
      gridApi?.deselectAll();
      setViewMode(VIEW_MODE.READ);
      navigate(`${basePath}`);
      return;
    }

    if (!entityId || !shouldUpdate) return;

    setShowModal(MODAL_STATE.EDIT);
    navigate(`${basePath}/${entityId}`);
    setViewMode(keepEditMode ? VIEW_MODE.EDIT : VIEW_MODE.READ);
    findAndSelectRecipeById(entityId);
  };

  const findAndSelectRecipeById = (recipeId: number) => {
    // In tree mode, row IDs are "recipe-{RecipeId}"
    const rowNode = gridApi?.getRowNode(`recipe-${recipeId}`);

    if (rowNode) {
      selectRecipeNode(rowNode);
      setPendingSelectRecipeId(null);
      return;
    }

    // Node not found yet - store the ID and wait for data to arrive via SignalR
    setPendingSelectRecipeId(recipeId);
  };

  const selectRecipeNode = (node: IRowNode) => {
    // If this is a child node, expand the parent first
    if (node.parent && !node.parent.expanded) {
      node.parent.setExpanded(true);
    }
    gridApi?.ensureNodeVisible(node, "middle");
    node.setSelected(true);
    if (node.data) {
      setRecipe(toRecipe(node.data));
    }
  };

  const getContextMenuItems = (
    params: GetContextMenuItemsParams,
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const data = params.node?.data;

    if (!data) {
      return [];
    }

    params.api.deselectAll();
    params.node?.setSelected(true);
    setRecipe(toRecipe(data));

    showContextMenu(
      params.event as unknown as React.MouseEvent,
      toRecipe(data),
      "recipeContext",
    );

    return [];
  };

  const handleDeleteRecipe = () => {
    const recipeId = deleteModalData.recipe?.RecipeId;
    if (recipeId == null) return;

    deleteMutation.mutate(recipeId, {
      onSuccess: () => {
        onModalHide({ shouldUpdate: true, close: true });
        setDeleteModalData({ open: false, recipe: null });
      },
      onError: onMutationError,
    });
  };

  const handleDeleteFamily = () => {
    const originalRecipeId = deleteFamilyModalData.originalRecipeId;
    if (originalRecipeId == null) return;

    deleteFamilyMutation.mutate(originalRecipeId, {
      onSuccess: () => {
        onModalHide({ shouldUpdate: true, close: true });
        setDeleteFamilyModalData({
          open: false,
          originalRecipeId: null,
          familyName: null,
        });
      },
      onError: onMutationError,
    });
  };

  const handleToggleFamilyEnableConfirm = () => {
    const { originalRecipeId, isEnable } = toggleFamilyModalData;
    if (originalRecipeId == null) return;

    setFamilyEnableMutation.mutate(
      { originalRecipeId, isEnable },
      {
        onSuccess: () => {
          setToggleFamilyModalData({
            open: false,
            originalRecipeId: null,
            familyName: null,
            isEnable: false,
          });
        },
        onError: onMutationError,
      },
    );
  };

  const handleCloneRecipe = (formData: { recipeName: string }) => {
    if (!cloneModalData.recipe?.RecipeId) return;

    cloneMutation.mutate(
      {
        recipeId: cloneModalData.recipe.RecipeId,
        recipeName: formData.recipeName,
      },
      {
        onSuccess: (resp) => {
          const newRecipeId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
          setCloneModalData({ open: false, recipe: null });
          resetCloneForm({ recipeName: "" });
          if (newRecipeId) {
            findAndSelectRecipeById(newRecipeId);
          }
        },
        onError: onMutationError,
      },
    );
  };

  const handleUpgradeRecipe = () => {
    if (!upgradeModalData.recipe?.RecipeId) return;

    upgradeMutation.mutate(upgradeModalData.recipe.RecipeId, {
      onSuccess: (resp) => {
        const newRecipeId = resp.data?.tables?.[0]?.rows?.[0]?.Id;
        setUpgradeModalData({ open: false, recipe: null });
        if (newRecipeId) {
          findAndSelectRecipeById(newRecipeId);
        }
      },
      onError: onMutationError,
    });
  };

  useEffect(() => {
    if (isLoading) {
      if (recipeId && recipeId !== "new") {
        const recipeIdNum = Number(recipeId);

        // Find recipe node by ID
        const recipeNode = gridApi?.getRowNode(`recipe-${recipeIdNum}`);

        if (recipeNode && recipeNode.data) {
          gridApi?.ensureIndexVisible(recipeNode.rowIndex ?? 0, "middle");
          recipeNode.setSelected(true);
          setRecipe(toRecipe(recipeNode.data));
          setShowModal(MODAL_STATE.EDIT);
          setViewMode(VIEW_MODE.READ);
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "recipeContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <HelmetDexteel title="Recipes" />
      <Box sx={{ p: 2 }} onContextMenu={(e) => e.preventDefault()}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Recipes
        </Typography>

        <Group
          id="recipe-table-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 140px)" }}
        >
          <Panel id="table" minSize={20}>
            <Stack spacing={2} sx={{ height: "100%" }}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Grid container alignItems="center">
                  <Grid item xs={12}>
                    <TextFieldSearchFilter
                      value={filterValue}
                      onChange={(val) => {
                        setFilterValue(val);
                        gridApi?.setGridOption("quickFilterText", val);
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Box
                sx={{
                  flex: 1,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <AgGridReact<RecipeTreeRow>
                  rowData={treeData}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    ...defaultColDef,
                    sortable: false,
                  }}
                  autoGroupColumnDef={autoGroupColumnDef}
                  treeData={true}
                  getDataPath={getDataPath}
                  getRowId={getRowId}
                  groupDefaultExpanded={0}
                  onGridReady={(params) => setGridApi(params.api)}
                  loading={isLoading}
                  onRowSelected={onRowSelected}
                  {...defaultGridOptions}
                  getContextMenuItems={getContextMenuItems}
                  suppressContextMenu={false}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNewRecipe}
                >
                  NEW RECIPE
                </Button>
              </Box>
            </Stack>
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          <Panel id="details" minSize={25}>
            <Paper
              elevation={1}
              sx={{
                height: "100%",
                overflow: "auto",
              }}
            >
              <RecipeDetails
                recipe={recipe}
                recipes={recipes}
                modal={showModal}
                onHide={onModalHide}
              />
            </Paper>
          </Panel>
        </Group>
      </Box>

      {/* Delete Confirmation Modal */}
      <MesfModal
        title="CONFIRM DELETE"
        open={deleteModalData.open}
        handleClose={() => setDeleteModalData({ open: false, recipe: null })}
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          {isLoading ? (
            <CenteredLazyLoading />
          ) : (
            <Typography variant="body1">
              Are you sure you want to delete{" "}
              <strong>{deleteModalData.recipe?.RecipeName}</strong>? This action
              cannot be undone.
            </Typography>
          )}
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() =>
                  setDeleteModalData({ open: false, recipe: null })
                }
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDeleteRecipe}
                disabled={isLoading}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      {/* Delete Family Confirmation Modal */}
      <MesfModal
        title="CONFIRM DELETE FAMILY"
        open={deleteFamilyModalData.open}
        handleClose={() =>
          setDeleteFamilyModalData({
            open: false,
            originalRecipeId: null,
            familyName: null,
          })
        }
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          {isLoading ? (
            <CenteredLazyLoading />
          ) : (
            <Typography variant="body1">
              Are you sure you want to delete the recipe family{" "}
              <strong>{deleteFamilyModalData.familyName}</strong> and all its
              versions? This action cannot be undone.
            </Typography>
          )}
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() =>
                  setDeleteFamilyModalData({
                    open: false,
                    originalRecipeId: null,
                    familyName: null,
                  })
                }
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDeleteFamily}
                disabled={isLoading}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      {/* Clone Confirmation Modal - Creates new independent recipe */}
      <MesfModal
        title="CLONE AS NEW RECIPE"
        open={cloneModalData.open}
        handleClose={() => {
          setCloneModalData({ open: false, recipe: null });
          resetCloneForm({ recipeName: "" });
        }}
        maxWidth="sm"
      >
        <form onSubmit={handleCloneSubmit(handleCloneRecipe)}>
          <MesfModal.Content sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Typography variant="body1">
                You are about to create a new independent recipe from{" "}
                <strong>{cloneModalData.recipe?.RecipeName}</strong>. This will
                create a completely separate recipe family.
              </Typography>
              <Controller
                name="recipeName"
                control={cloneFormControl}
                rules={{
                  required: "Recipe name is required",
                  validate: (value) => {
                    const trimmedValue = value.trim().toLowerCase();
                    const isDuplicate = recipes.some(
                      (r) => r.RecipeName.toLowerCase() === trimmedValue,
                    );
                    return isDuplicate
                      ? "A recipe with this name already exists"
                      : true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="New Recipe Name"
                    fullWidth
                    size="small"
                    error={!!cloneFormErrors.recipeName}
                    helperText={cloneFormErrors.recipeName?.message}
                  />
                )}
              />
            </Stack>
          </MesfModal.Content>
          <MesfModal.Actions sx={{ p: "20px 30px" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => {
                    setCloneModalData({ open: false, recipe: null });
                    resetCloneForm({ recipeName: "" });
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item md={3} xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isCloneFormValid}
                >
                  PROCEED
                </Button>
              </Grid>
            </Grid>
          </MesfModal.Actions>
        </form>
      </MesfModal>

      {/* Upgrade Confirmation Modal - Creates new version in same family */}
      <MesfModal
        title="CREATE NEW VERSION"
        open={upgradeModalData.open}
        handleClose={() => setUpgradeModalData({ open: false, recipe: null })}
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          <Typography variant="body1">
            You are about to create a new version from{" "}
            <strong>{upgradeModalData.recipe?.RecipeName}</strong>. The new
            version will be created as a draft (not current). Do you want to
            proceed?
          </Typography>
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() =>
                  setUpgradeModalData({ open: false, recipe: null })
                }
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleUpgradeRecipe}
              >
                PROCEED
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      {/* Set as Current Confirmation Modal */}
      <MesfModal
        title="SET AS CURRENT VERSION"
        open={setCurrentModalData.open}
        handleClose={() =>
          setSetCurrentModalData({ open: false, recipe: null })
        }
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          <Stack spacing={2}>
            <Typography variant="body1">
              You are about to set{" "}
              <strong>{setCurrentModalData.recipe?.RecipeName}</strong> as the
              current version.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action will:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Make this version the active one for production
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Enable this recipe (it will appear in the Crane app)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                The previous current version will no longer be active
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Do you want to proceed?
            </Typography>
          </Stack>
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() =>
                  setSetCurrentModalData({ open: false, recipe: null })
                }
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSetAsCurrent}
              >
                CONFIRM
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      {/* Enable/Disable Family Confirmation Modal */}
      <MesfModal
        title={
          toggleFamilyModalData.isEnable
            ? "ENABLE RECIPE FAMILY"
            : "DISABLE RECIPE FAMILY"
        }
        open={toggleFamilyModalData.open}
        handleClose={() =>
          setToggleFamilyModalData({
            open: false,
            originalRecipeId: null,
            familyName: null,
            isEnable: false,
          })
        }
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          <Typography variant="body1">
            You are about to{" "}
            {toggleFamilyModalData.isEnable ? "enable" : "disable"} recipe
            family <strong>{toggleFamilyModalData.familyName}</strong>. This
            recipe will{" "}
            {toggleFamilyModalData.isEnable ? "appear" : "no longer appear"} in
            the Crane app. Do you want to proceed?
          </Typography>
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() =>
                  setToggleFamilyModalData({
                    open: false,
                    originalRecipeId: null,
                    familyName: null,
                    isEnable: false,
                  })
                }
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleToggleFamilyEnableConfirm}
              >
                {toggleFamilyModalData.isEnable ? "ENABLE" : "DISABLE"}
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>

      <ErrorModal
        title="Recipe Error"
        error={error}
        onHide={() => setError("")}
      />
    </>
  );
};
